import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { BackIcon, Box, LoadingSpinner, PagePanel } from 'edenred-ui';
import { useTranslation } from 'react-i18next';

import { useGetMassUploadDetailsQuery } from '@epi-api/massUploadHistory/massUploadHistoryApi';
import { Pagination } from '@epi-shared/components';
import { goToPage } from '@epi-actions/navigation';
import { routerPaths } from '@epi-constants/routerPaths';

import { MassUploadDetailsSummary } from './components/MassUploadDetailsSummary';
import { MassUploadDetailsItemsTable } from './components/MassUploadDetailsItemsTable';
import { PageTitle, BackIconWrapper } from './MassUploadDetails.styles';
import { useOnboardingWizard } from '@epi-shared/hooks/useOnboardingWizard';

export const MassUploadDetails = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { uploadId } = useParams<{ uploadId: string }>();
  const parsedUploadId = Number.parseInt(uploadId, 10);
  const { isOnBoardingActive, flags, handleGoOnboardingPayment } =
    useOnboardingWizard();

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const onClickBack = () =>
    dispatch(goToPage(routerPaths.beneficiariesHrImportHistory));

  const footer = isOnBoardingActive
    ? {
        backText: t('components.onboarding.buttons.back'),
        submitText: t('components.onboarding.buttons.next'),
        onBack: onClickBack,
        onSubmit: handleGoOnboardingPayment,
        disableSubmit: !flags?.isEmployeesSet,
        submitButtonWidth: '240px'
      }
    : undefined;

  const {
    data: detailsData,
    isLoading,
    isError
  } = useGetMassUploadDetailsQuery(
    { uploadId: parsedUploadId, page: currentPage, pageSize },
    { skip: Number.isNaN(parsedUploadId) }
  );

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isError) {
    return <Box p={4}>{t('containers.mass_upload.details.error')}</Box>;
  }

  return (
    <Box>
      <PagePanel footer={footer}>
        <Box display="flex" alignItems="center" mb={3}>
          <Box pr={2} pb={0.5} onClick={onClickBack}>
            <BackIconWrapper>
              <BackIcon />
            </BackIconWrapper>
          </Box>
          <PageTitle>{t('containers.mass_upload.details.title')}</PageTitle>
        </Box>

        {detailsData && (
          <>
            <MassUploadDetailsSummary summary={detailsData.summary} />
            <MassUploadDetailsItemsTable items={detailsData.items.items} />

            {detailsData.items.totalCount > 0 && (
              <Box mt={3} display="flex" justifyContent="center">
                <Pagination
                  page={currentPage}
                  count={Math.ceil(detailsData.items.totalCount / pageSize)}
                  defaultRowsPerPage={pageSize}
                  onChangePage={(pageNumber: number) => {
                    setCurrentPage(pageNumber);
                    window.scrollTo(0, 0);
                  }}
                />
              </Box>
            )}
          </>
        )}

        {!detailsData && !isLoading && (
          <Box>{t('containers.mass_upload.details.noData')}</Box>
        )}
      </PagePanel>
    </Box>
  );
};
